import { smsCampaignsApi } from '@/store/smsCampaignService';
import {
  STEP_SETUP,
  STEP_RECIPIENTS_MANUAL,
  STEP_REVIEW,
} from '@/pages/smsCampaigns/createCampaign/constants/steps';
import { campaignDoesNotBelongToCompany } from '@/pages/smsCampaigns/util/helpers';

const initializeForm = () => ({
  step: STEP_SETUP,
  isSaving: false,
  isLoading: false,
  data: null,
  recipientsType: null,
  recipientGroups: [],
});

const initializeCompanySettings = () => ({
  isLoading: false,
  data: null,
});

const InitialState = {
  form: initializeForm(),
  companySettings: initializeCompanySettings(),
};

const mutations = {
  // form mutations
  resetForm: (state) => Object.assign(state, {
    form: initializeForm(),
  }),
  nextStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_RECIPIENTS_MANUAL;
        break;
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_REVIEW;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  backStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_SETUP;
        break;
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_SETUP;
        break;
      case STEP_REVIEW:
        nextStep = STEP_RECIPIENTS_MANUAL;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  gotoStep: (state, step) => Object.assign(state, {
    form: Object.assign(state.form, {
      step,
    }),
  }),
  setLoading: (state, isLoading) => Object.assign(state, {
    form: Object.assign(state.form, {
      isLoading,
    }),
  }),
  setSaving: (state, isSaving) => Object.assign(state, {
    form: Object.assign(state.form, {
      isSaving,
    }),
  }),
  setCampaignData: (state, data) => Object.assign(state, {
    form: Object.assign(state.form, {
      data,
    }),
  }),
  setRecipientGroups: (state, recipientGroups) => Object.assign(state, {
    form: Object.assign(state.form, {
      recipientGroups,
    }),
  }),
  appendRecipient: (state, item) => {
    const foundGroup = state.form.recipientGroups.find((group) => group.uid === item.uid);

    // add if item does not exist in the list, else keep same list
    const newRecipientsGroup = !foundGroup
      ? [
        ...state.form.recipientGroups,
        item,
      ]
      : state.form.recipientGroups;

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientsGroup,
      }),
    });
  },
  removeRecipient: (state, id) => {
    // keep recipients that do not match provided id
    const newRecipientList = state.form.recipientGroups.filter((item) => item.uid !== id);

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  removeRecipientsByOwnerAndSource: (state, { owner, source, excludedIds = [] }) => {
    const newRecipientList = state.form.recipientGroups.filter(
      (item) => {
        // keep recipient if uid is excluded
        if (excludedIds.includes(item.uid)) {
          return true;
        }

        // keep recipient if it does not have matching owner & source
        if (!(item.owner === owner && item.source === source)) {
          return true;
        }

        return false;
      },
    );

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  removeShareholderRangeRecipients: (state) => {
    const newRecipientList = state.form.recipientGroups
      .filter((item) => {
        // keep recipients that are not shareholders
        if (item.source !== 'shareholders') {
          return true;
        }

        const isShareholderRange = typeof item.name === 'string'
          && (
            item.name.toLowerCase().includes('shareholders with greater than')
            || item.name.toLowerCase().includes('shareholders with less than')
          );

        // keep recipients that are not a shareholder range
        return !isShareholderRange;
      });

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  removeManualRecipients: (state) => {
    // keep recipients that are not manual
    const newRecipientList = state.form.recipientGroups.filter((item) => item.source !== 'manual');

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientGroups: newRecipientList,
      }),
    });
  },
  // company settings mutations
  setCompanySettingsLoading: (state, isLoading) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      isLoading,
    }),
  }),
  setCompanySettingsData: (state, data) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      data,
    }),
  }),
};

const actions = {
  async updateCampaign(context, { id, payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      await smsCampaignsApi('patch', `/sms/campaigns/${id}`, {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
        data: payload,
      });
      // re-populate campaign data
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async createCampaign(context, { payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      const res = await smsCampaignsApi('post', '/sms/campaigns', {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
        data: payload,
      });

      const campaignId = res.data.campaign_uid;

      await context.dispatch('getCampaign', {
        id: campaignId,
        isLoading: false,
      });

      return campaignId;
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async forwardCampaign(context, payload) {
    try {
      context.commit('setSaving', true);

      const data = context.state.form.data;

      const fullPayload = {
        // step setup
        name: data.name,
        run_at: data.run_at,
        subject: data.subject,
        // step recipients type
        type: data.type,
        // step recipients manual
        recipient_groups: [],
        // step recipients auto
        trigger_condition: data.trigger_condition,
        trigger_condition_threshold: data.trigger_condition_threshold,
        // add fields from payload
        ...payload,
      };

      // create campaign and return campaign id
      return await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: fullPayload,
      });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async createCampaignWithRecipient(context, { campaignPayload, recipientPayload }) {
    try {
      context.commit('setSaving', true);

      const postRes = await smsCampaignsApi('post', '/recipient-groups/uuid', {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
        data: recipientPayload,
      });

      const recipientGroupId = postRes.data.recipient_group_uid;

      const fullCampaignPayload = {
        ...campaignPayload,
        recipient_groups: [
          recipientGroupId,
        ],
      };

      // create campaign and return campaign id
      return await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: fullCampaignPayload,
      });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async getCampaign(context, { id, enableLoading = true }) {
    try {
      if (enableLoading) context.commit('setLoading', true);

      const res = await smsCampaignsApi('get', `/sms/campaigns/${id}`, {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
      });
      const data = res.data.data;

      // set recipient groups
      if (data.recipient_groups) {
        context.commit('setRecipientGroups', data.recipient_groups);
      } else {
        context.commit('setRecipientGroups', []);
      }

      context.commit('setCampaignData', data);
    } catch (error) {
      if (campaignDoesNotBelongToCompany(error)) {
        throw new Error('UnauthorizedCompany');
      } else {
        throw error;
      }
    } finally {
      if (enableLoading) context.commit('setLoading', false);
    }
  },
  async getCompanySettings(context, forceRefesh = false) {
    try {
      const companyId = context.rootGetters['company/company_id'];

      // exit function if we have already fetched settings for current company
      if (
        context.state.companySettings.data
        && context.state.companySettings.data.company_id === companyId
        && !forceRefesh
      ) return;

      context.commit('setCompanySettingsLoading', true);

      const res = await smsCampaignsApi('get', '/settings', {
        params: {
          company_id: companyId,
        },
      });
      const data = res.data.data;

      // add company_id to response so we can detect
      // if we need to refresh company settings (if company has changed)
      data.company_id = companyId;

      context.commit('setCompanySettingsData', data);
    } finally {
      context.commit('setCompanySettingsLoading', false);
    }
  },
  async updateCompanySettings(context, payload) {
    await smsCampaignsApi('patch', '/settings', {
      params: {
        company_id: context.rootGetters['company/company_id'],
      },
      data: payload,
    });
    // refresh company data
    await context.dispatch('getCompanySettings', true);
  },
  async uploadContactCsv(context, payload) {
    const postRes = await smsCampaignsApi('post', '/manual-contacts/csv', {
      params: {
        company_id: context.rootGetters['company/company_id'],
        campaign_type: 'SMS',
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: payload,
    }, 'data');
    const recipientGroupList = postRes.data;
    recipientGroupList.forEach(async (item) => {
      const recipientGroupId = item.recipient_group_uid;
      const getRes = await smsCampaignsApi('get', `/recipient-groups/${recipientGroupId}`, {
        params: {
          company_id: context.rootGetters['company/company_id'],
        },
      });
      context.commit('appendRecipient', getRes.data.data);
    });
  },
  async addManualContact(context, payload) {
    const postRes = await smsCampaignsApi('post', '/manual-contacts/single', {
      params: {
        company_id: context.rootGetters['company/company_id'],
      },
      data: payload,
    }, 'data');
    const recipientGroupId = postRes.data.recipient_group_uid;
    const getRes = await smsCampaignsApi('get', `/recipient-groups/${recipientGroupId}`, {
      params: {
        company_id: context.rootGetters['company/company_id'],
      },
    });
    context.commit('appendRecipient', getRes.data.data);
  },
  async addRecipientGroupByUuidAndType(context, payload) {
    const postRes = await smsCampaignsApi('post', '/recipient-groups/single', {
      params: {
        company_id: context.rootGetters['company/company_id'],
      },
      data: payload,
    }, 'data');
    const recipientGroupId = postRes.data.uid;
    const getRes = await smsCampaignsApi('get', `/recipient-groups/${recipientGroupId}`, {
      params: {
        company_id: context.rootGetters['company/company_id'],
      },
    });

    context.commit('appendRecipient', getRes.data.data);
  },
};

const getters = {
  manualRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'manual'),
  csvRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'csv'),
  contactRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'contacts'),
  shareholderRecipients: (state) => state.form.recipientGroups.filter((item) => item.source === 'shareholders'),
  totalRecipientCount: (state) => state.form.recipientGroups
    .reduce((acc, item) => acc + item.count, 0),
  campaignsFeatureIsSetup: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.sms_enabled,
  campaignsFeatureIsActive: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.client_status
    && state.companySettings.data.client_status === 'active',
  // object map of shareholder recipients based on source_uuid_type property
  shareholdersSourceTypeMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source_uuid_type === 'shareholder') {
      acc[item.source_uuid] = true;
    }
    return acc;
  }, {}),
  // object map of shareholder recipients based on source property
  shareholdersSourceMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source === 'shareholders') {
      acc[item.uid] = true;
    }
    return acc;
  }, {}),
  // object map of contact recipients based on source_uuid_type property
  contactsSourceTypeMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source_uuid_type === 'contact') {
      acc[item.source_uuid] = true;
    }
    return acc;
  }, {}),
  // object map of contact recipients based on source property
  contactsSourceMap: (state) => state.form.recipientGroups.reduce((acc, item) => {
    if (item.source === 'contacts') {
      acc[item.uid] = true;
    }

    return acc;
  }, {}),
};

export default {
  state: InitialState,
  mutations,
  actions,
  getters,
  namespaced: true,
};
